// Suchfelder (Partials/ContentElements/SearchBox.html) in Header und Hero-Slider:
// senden ?q=... an die Suchseite (Offer-Liste mit settings.list.searchFromUrl).
// - Desktop: der Header-Button öffnet #header-search über der Navigation (schließt per Klick außerhalb/Esc)
// - Mobil: der Lupen-Button klappt #mobile-search unter dem Header auf (schließt per Klick außerhalb/Esc)
// - Auf der Suchseite selbst werden die Felder mit dem aktuellen Begriff vorbelegt
//   (serverseitig nicht möglich: Header und Hero liegen im Seitencache)
// - Felder mit Trefferliste ([data-site-search-suggest]) laden beim Tippen das HTML-Fragment
//   aus OfferController::suggestAction() und klappen es als Combobox-Liste unter dem Feld auf
const MIN_LENGTH = 2;
const SUGGEST_DEBOUNCE_MS = 250;
// Mobil steht die Liste im sticky Header (overflow-hidden). vh/dvh berücksichtigen weder die
// Bildschirmtastatur noch (iOS) die Browserleisten, daher Höhe am visual viewport messen,
// sonst ragt die Liste unter Tastatur/Rand und hat selbst keinen Überlauf zum Scrollen
const VIEWPORT_GAP_PX = 12;
const MIN_LIST_PX = 120;

export function initSiteSearch() {
  const forms = document.querySelectorAll('form[data-site-search-form]');
  if (forms.length === 0) return;

  const currentQuery = new URLSearchParams(window.location.search).get('q')?.trim() ?? '';

  forms.forEach((form) => {
    const input = form.querySelector('[data-site-search-input]');
    if (!input) return;

    const actionPath = new URL(form.getAttribute('action') || window.location.href, window.location.href).pathname;
    if (currentQuery !== '' && actionPath === window.location.pathname) {
      input.value = currentQuery;
    }

    // "required"/"minlength" lassen reine Leerzeichen durch
    input.addEventListener('input', () => input.setCustomValidity(''));
    form.addEventListener('submit', (event) => {
      input.value = input.value.trim();
      if (input.value.length < MIN_LENGTH) {
        event.preventDefault();
        input.setCustomValidity(`Bitte mindestens ${MIN_LENGTH} Zeichen eingeben.`);
        input.reportValidity();
      }
    });

    const root = form.closest('[data-site-search]');
    const list = root?.querySelector('[data-site-search-suggest]');
    if (root && list && list.dataset.siteSearchSuggest) {
      initSuggest(root, input, list);
    }
  });

  initHeaderSearch();
  initMobileSearch();
}

function initSuggest(root, input, list) {
  const endpoint = list.dataset.siteSearchSuggest;
  let timer = null;
  let controller = null;
  let lastQuery = '';
  let active = -1;

  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-expanded', 'false');
  input.setAttribute('aria-controls', list.id);
  input.setAttribute('autocomplete', 'off');

  const options = () => [...list.querySelectorAll('[data-suggest-option]')];

  const setActive = (index) => {
    active = index;
    options().forEach((option, i) => {
      option.id ||= `${list.id}-option-${i}`;
      option.setAttribute('aria-selected', i === index ? 'true' : 'false');
      if (i === index) {
        input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      }
    });
    if (index < 0) input.removeAttribute('aria-activedescendant');
  };

  // Ab "nav" hängt die Liste absolut über dem Inhalt, dort gilt max-h-[60vh] aus dem Partial
  const fitToViewport = () => {
    if (list.hidden) return;
    if (window.getComputedStyle(list).position === 'absolute') {
      list.style.maxHeight = '';
      return;
    }
    const viewport = window.visualViewport;
    const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    const space = bottom - list.getBoundingClientRect().top - VIEWPORT_GAP_PX;
    list.style.maxHeight = `${Math.max(MIN_LIST_PX, Math.floor(space))}px`;
  };

  (window.visualViewport ?? window).addEventListener('resize', fitToViewport);
  window.visualViewport?.addEventListener('scroll', fitToViewport);

  const show = () => {
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    fitToViewport();
  };

  const hide = () => {
    list.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    setActive(-1);
  };

  const load = async () => {
    const query = input.value.trim();
    if (query.length < MIN_LENGTH) {
      controller?.abort();
      lastQuery = '';
      list.innerHTML = '';
      hide();
      return;
    }
    if (query === lastQuery) {
      if (list.innerHTML.trim() !== '') show();
      return;
    }

    lastQuery = query;
    controller?.abort();
    controller = new AbortController();
    const url = new URL(endpoint, window.location.href);
    url.searchParams.set('q', query);

    try {
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      list.innerHTML = await response.text();
      setActive(-1);
      if (list.innerHTML.trim() !== '' && document.activeElement === input) {
        show();
      } else {
        hide();
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Ohne Liste bleibt das normale Absenden an die Suchseite
      lastQuery = '';
      hide();
    }
  };

  input.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(load, SUGGEST_DEBOUNCE_MS);
  });

  input.addEventListener('focus', () => {
    if (lastQuery !== '' && input.value.trim() === lastQuery && list.innerHTML.trim() !== '') show();
  });

  input.addEventListener('keydown', (event) => {
    const count = options().length;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (list.hidden) {
        load();
      } else if (count > 0) {
        setActive(active + 1 >= count ? 0 : active + 1);
      }
    } else if (event.key === 'ArrowUp') {
      if (list.hidden || count === 0) return;
      event.preventDefault();
      setActive(active <= 0 ? count - 1 : active - 1);
    } else if (event.key === 'Enter' && !list.hidden && active >= 0) {
      // Ohne Auswahl schickt Enter das Formular wie gewohnt an die Suchseite
      event.preventDefault();
      options()[active]?.click();
    } else if (event.key === 'Escape' && !list.hidden) {
      // Erst die Liste schließen, erst das nächste Esc schließt die Header-Suche
      event.stopPropagation();
      hide();
    }
  });

  document.addEventListener('click', (event) => {
    if (!list.hidden && !root.contains(event.target)) hide();
  });
}

// transitionend blubbert auch von Kindern hoch (Hover-Transitions von Feld/Buttons),
// nur die Transition des Elements selbst zählt. Fallback per Timeout, falls keine läuft
// (z. B. per Breakpoint ausgeblendet), sonst bliebe "hidden" ungesetzt und der Toggle hängt.
const TRANSITION_FALLBACK_MS = 400;

function onOwnTransitionEnd(element, callback) {
  let timer = null;
  const finish = () => {
    element.removeEventListener('transitionend', handler);
    window.clearTimeout(timer);
    callback();
  };
  const handler = (event) => {
    if (event.target === element) finish();
  };
  element.addEventListener('transitionend', handler);
  timer = window.setTimeout(finish, TRANSITION_FALLBACK_MS);
}

function initHeaderSearch() {
  const toggle = document.querySelector('[data-header-search-toggle]');
  const overlay = document.querySelector('[data-header-search]');
  if (!toggle || !overlay) return;

  const input = overlay.querySelector('[data-site-search-input]');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Rechte Kante des Feldes = rechte Kante des Such-Buttons, damit Telefon und
  // Favoriten sichtbar bleiben. Die Lage des Buttons hängt vom Breakpoint ab, daher per Messung:
  // - ab 3xl einzeilig: Overlay endet am Button
  // - darunter stehen Button/Telefon/Favoriten über der Navigation: Overlay deckt nur die
  //   Navigationszeile, die aber auf ganzer Breite; das Feld endet per padding am Button
  const alignToToggle = () => {
    const container = overlay.offsetParent;
    const nav = container?.querySelector('.main-nav');
    if (!container || !nav) return;
    const box = container.getBoundingClientRect();
    const toggleBox = toggle.getBoundingClientRect();
    const navBox = nav.getBoundingClientRect();
    const gap = `${Math.max(0, box.right - toggleBox.right)}px`;
    const sameRow = toggleBox.bottom > navBox.top;

    overlay.style.top = sameRow ? '' : `${navBox.top - box.top}px`;
    overlay.style.right = sameRow ? gap : '0px';
    overlay.style.paddingRight = sameRow ? '' : gap;
  };

  window.addEventListener('resize', () => {
    if (!overlay.hidden) alignToToggle();
  });

  const open = () => {
    overlay.hidden = false;
    alignToToggle();
    toggle.setAttribute('aria-expanded', 'true');
    // Unter 3xl ragt das Feld in die Zeile des Buttons; der Button selbst bleibt sonst angeschnitten sichtbar
    toggle.classList.add('invisible');
    // Erst nach dem Einblenden die Klasse setzen, damit die clip-path-Transition läuft;
    // danach "is-revealed" (ohne clip-path), damit die Trefferliste sichtbar überhängen kann
    window.requestAnimationFrame(() => {
      overlay.classList.add('is-open');
      input?.focus({ preventScroll: true });
      if (reducedMotion.matches) {
        overlay.classList.add('is-revealed');
      } else {
        onOwnTransitionEnd(overlay, () => {
          if (overlay.classList.contains('is-open')) overlay.classList.add('is-revealed');
        });
      }
    });
  };

  const close = ({ returnFocus = false } = {}) => {
    if (overlay.hidden) return;
    // Von "clip-path: none" (is-revealed) aus läuft keine Transition und damit kein transitionend:
    // erst auf inset(0) zurück und per Reflow übernehmen, dann zuklappen
    overlay.classList.remove('is-revealed');
    void overlay.offsetWidth;
    overlay.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.classList.remove('invisible');
    if (reducedMotion.matches) {
      overlay.hidden = true;
    } else {
      onOwnTransitionEnd(overlay, () => {
        if (!overlay.classList.contains('is-open')) overlay.hidden = true;
      });
    }
    if (returnFocus) toggle.focus();
  };

  toggle.addEventListener('click', () => (overlay.hidden ? open() : close()));

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !overlay.hidden) close({ returnFocus: true });
  });

  document.addEventListener('click', (event) => {
    if (!overlay.hidden && !overlay.contains(event.target) && !toggle.contains(event.target)) close();
  });
}

// Gleiches Prinzip wie initHeaderSearch(). Das mobile Menü (main.js) schließt sich beim
// Tippen auf die Lupe über seinen eigenen Klick-außerhalb-Handler, umgekehrt dieser hier.
function initMobileSearch() {
  const toggle = document.querySelector('[data-mobile-search-toggle]');
  const bar = document.querySelector('[data-mobile-search]');
  if (!toggle || !bar) return;

  const input = bar.querySelector('[data-site-search-input]');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const open = () => {
    bar.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
    // Fokus noch im Klick-Handler, sonst öffnet iOS die Tastatur nicht
    input?.focus({ preventScroll: true });
    window.requestAnimationFrame(() => {
      bar.classList.add('is-open');
      if (reducedMotion.matches) {
        bar.classList.add('is-revealed');
      } else {
        onOwnTransitionEnd(bar, () => {
          if (bar.classList.contains('is-open')) bar.classList.add('is-revealed');
        });
      }
    });
  };

  const close = ({ returnFocus = false } = {}) => {
    if (bar.hidden) return;
    bar.classList.remove('is-revealed', 'is-open');
    toggle.setAttribute('aria-expanded', 'false');
    if (reducedMotion.matches) {
      bar.hidden = true;
    } else {
      onOwnTransitionEnd(bar, () => {
        if (!bar.classList.contains('is-open')) bar.hidden = true;
      });
    }
    if (returnFocus) toggle.focus();
  };

  toggle.addEventListener('click', () => (bar.hidden ? open() : close()));

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !bar.hidden) close({ returnFocus: true });
  });

  document.addEventListener('click', (event) => {
    if (!bar.hidden && !bar.contains(event.target) && !toggle.contains(event.target)) close();
  });
}
